/**
 * NAV REGISTRY - single source of truth for all navigable routes.
 *
 * HOW TO ADD A NEW ROUTE:
 *   1. Add a NavEntry here with the context name(s) it should appear in.
 *   2. Add the React Router <Route> in App.tsx.
 *
 * visibleIn: list of context `name` values (from the contexts DB table).
 *            Use ['*'] to show in every context.
 */

import {
  LayoutDashboard,
  List,
  Upload,
  Settings,
  Network,
  Database,
  Package,
  FileCode,
  BarChart3,
  type LucideIcon,
  HeartPulse,
  Plug,
  Layers,
  ShieldCheck,
  Activity,
  Building2,
  MapPin,
} from 'lucide-react'

export interface NavSubItem {
  path: string
  icon: LucideIcon
  label: string
}

/** A standalone nav entry (single link) */
export interface StandaloneNavEntry {
  key: string
  label: string
  icon: LucideIcon
  path: string
  /** Context names this entry is visible in. Use ['*'] for all contexts. */
  visibleIn: string[]
  subItems?: never
}

/** A grouped nav entry (expandable section with sub-links) */
export interface GroupNavEntry {
  key: string
  label: string
  icon: LucideIcon
  subItems: NavSubItem[]
  /** Context names this entry is visible in. Use ['*'] for all contexts. */
  visibleIn: string[]
  path?: never
}

export type NavEntry = StandaloneNavEntry | GroupNavEntry

/**
 * All registered nav modules, in definition order.
 * Add an entry here + a route in App.tsx - that's all that's needed.
 */
export const NAV_REGISTRY: NavEntry[] = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    path: '/',
    visibleIn: ['*'],
  },
  {
    key: 'admin-panel',
    label: 'Context Configurator',
    icon: Settings,
    path: '/admin',
    visibleIn: ['superadmin'],
  },
  {
    key: 'kong',
    label: 'Kong',
    icon: Network,
    visibleIn: ['superadmin'],
    subItems: [
      { path: '/admin/kong-health', icon: HeartPulse, label: 'Health' },
      { path: '/admin/kong-topology', icon: Network, label: 'Routing Topology' },
    ],
  },
  {
    key: 'systems',
    label: 'System Inventory',
    icon: List,
    visibleIn: ['cpq'],
    subItems: [
      { path: '/systems', icon: Database, label: 'Systems' },
      { path: '/products', icon: Package, label: 'Products' },
      { path: '/systems/business-logic', icon: FileCode, label: 'Business Logic' },
    ],
  },
  {
    key: 'service-inventory',
    label: 'Service Inventory',
    icon: ShieldCheck,
    visibleIn: ['service_assurance'],
    subItems: [
      { path: '/systems', icon: Database, label: 'Systems' },
      { path: '/capabilities', icon: Layers, label: 'Capabilities' },
      { path: '/systems/business-logic', icon: FileCode, label: 'Business Logic' },
    ],
  },
  {
    key: 'integration',
    label: 'Integration',
    icon: Upload,
    visibleIn: ['cpq', 'service_assurance', 'digital_experience', 'sales', 'operations', 'finance'],
    subItems: [
      { path: '/integration/data', icon: FileCode, label: 'Data Integration' },
      { path: '/integration/context-digester', icon: FileCode, label: 'Data Digest' },
    ],
  },
  {
    key: 'indirect-partners',
    label: 'Indirect Partners',
    icon: Building2,
    visibleIn: ['cpq'],
    subItems: [{ path: '/indirect-partners/lmaas-metros', icon: MapPin, label: 'LMaaS Metros' }],
  },
  {
    key: 'integration-admin',
    label: 'Integration Admin View',
    icon: BarChart3,
    path: '/admin/integration',
    visibleIn: ['superadmin'],
  },
  {
    key: 'api-integration',
    label: 'API Integration',
    icon: Plug,
    path: '/admin/api-integration',
    visibleIn: ['superadmin'],
  },
  {
    key: 'partner-test-health',
    label: 'Partner Test Health',
    icon: Activity,
    path: '/admin/partner-test-health',
    visibleIn: ['superadmin'],
  },
]

/** O(1) lookup by module_key */
export const NAV_REGISTRY_MAP: Record<string, NavEntry> = Object.fromEntries(NAV_REGISTRY.map((e) => [e.key, e]))
